import ConversationModel from 'Models/ConversationModel';
import { initialState as pinnedConversationsSliceInitialState } from 'Modules/conversation/store/pinned-conversations/index.slice';
import { PinnedConversationsSlice } from 'Modules/conversation/store/pinned-conversations/index.types';
import {
  getLastConversationId,
  removeDuplicateConversations,
  removePinnedConversations,
  sortAndRemoveDuplicatesConversations,
} from 'Modules/conversation/utils/conversationsListManagement';
import { StateCreator } from 'zustand';
import { CONVERSATIONS_LIST_CONVERSATIONS_PER_PAGE } from '../index.constants';
import {
  ConversationsListSliceState,
  ConversationsListSlice,
} from './index.types';
import { doesConversationMatchFilter } from './utils';

const initialState: ConversationsListSliceState = {
  conversationsList: [],
  isLoadingConversationsList: false,
  lastConversationId: undefined,
  hasMoreConversations: true,
  conversationsListFilter: 'all',
  totalConversations: 0,
  hasAttemptedConversationsFetch: false,
};

export const createConversationsListSlice: StateCreator<
  ConversationsListSlice & Partial<PinnedConversationsSlice>
> = (set, get) => ({
  ...initialState,
  appendConversationsList: (
    conversations,
    hasMoreConversations,
    pinnedConversations
  ) => {
    if (conversations.length === 0) {
      const updatedPinnedConversations = removeDuplicateConversations(
        pinnedConversations ?? get().pinnedConversations ?? []
      );

      set(() => ({
        hasMoreConversations: false,
        pinnedConversations: updatedPinnedConversations,
        totalConversations: updatedPinnedConversations.length,
      }));
      return;
    }

    set((state) => {
      const newConversationsList = sortAndRemoveDuplicatesConversations(
        removePinnedConversations(
          [...state.conversationsList, ...conversations],
          pinnedConversations ?? state.pinnedConversations ?? []
        )
      );
      const updatedPinnedConversations = removeDuplicateConversations(
        pinnedConversations ?? state.pinnedConversations ?? []
      );

      // For pagination, we should preserve the lastConversationId from the most recently appended page
      // instead of sorting all conversations and taking the last one
      const newLastConversationId =
        conversations.length > 0
          ? conversations[conversations.length - 1].id
          : getLastConversationId(newConversationsList);

      return {
        conversationsList: newConversationsList,
        lastConversationId: newLastConversationId,
        hasMoreConversations,
        pinnedConversations: updatedPinnedConversations,
        totalConversations:
          newConversationsList.length + updatedPinnedConversations.length,
      };
    });
  },
  addConversationToList: (newConversation) => {
    if (!newConversation?.id) {
      return;
    }

    if (
      !doesConversationMatchFilter(
        newConversation,
        get().conversationsListFilter
      )
    ) {
      return;
    }

    // Update pinned conversation if it exists
    if (get().updatePinnedConversation(newConversation)) {
      return;
    }

    // Check if conversation exists in regular list
    const regularConversationIndex = get().conversationsList.findIndex(
      (conversation) => conversation.id === newConversation.id
    );

    let updatedConversationsList = [...get().conversationsList];

    if (regularConversationIndex !== -1) {
      // Update existing conversation
      updatedConversationsList.splice(
        regularConversationIndex,
        1,
        newConversation
      );
    } else {
      // Add new conversation
      updatedConversationsList.push(newConversation);
    }

    updatedConversationsList = sortAndRemoveDuplicatesConversations(
      removePinnedConversations(
        updatedConversationsList,
        get().pinnedConversations
      )
    );

    set({
      conversationsList: updatedConversationsList,
      lastConversationId: getLastConversationId(updatedConversationsList),
    });
  },
  /**
   * @deprecated This method will be removed in a future version.
   * Use `appendConversationsList` or `mergeConversationsListFromPolling` instead.
   */
  setConversationsList: (conversations) => {
    set((state) => {
      if (conversations.length === 0) {
        return { hasMoreConversations: false };
      }

      const newConversationsList = sortAndRemoveDuplicatesConversations(
        removePinnedConversations(conversations, state.pinnedConversations)
      );

      return {
        conversationsList: newConversationsList,
        lastConversationId: getLastConversationId(newConversationsList),
      };
    });
  },
  /**
   * This method is designed to preserve pagination state while updating conversations.
   * It will:
   * - Update existing conversations with new data
   * - Add new conversations to the top
   * - Preserve all previously loaded pages
   * - Maintain the lastConversationId for pagination
   *
   * Warning: To be used solely by the polling mechanism.
   */
  mergeConversationsListFromPolling: (
    polledConversations,
    pinnedConversations
  ) => {
    set((state) => {
      const currentList = state.conversationsList;
      const currentPinned = state.pinnedConversations ?? [];

      const currentConversationsMap = new Map(
        currentList.map((conv) => [conv.id, conv])
      );

      const updatedConversations: ConversationModel[] = [];
      const newConversations: ConversationModel[] = [];

      polledConversations.forEach((polledConv) => {
        if (currentConversationsMap.has(polledConv.id)) {
          updatedConversations.push(polledConv);
          currentConversationsMap.set(polledConv.id, polledConv);
        } else {
          newConversations.push(polledConv);
        }
      });

      const mergedList = [
        ...newConversations,
        ...Array.from(currentConversationsMap.values()),
      ];

      const finalConversationsList = sortAndRemoveDuplicatesConversations(
        removePinnedConversations(
          mergedList,
          pinnedConversations ?? currentPinned
        )
      );

      const updatedPinnedConversations = removeDuplicateConversations(
        pinnedConversations ?? currentPinned
      );

      const shouldPreserveLastId =
        currentList.length > CONVERSATIONS_LIST_CONVERSATIONS_PER_PAGE;

      return {
        conversationsList: finalConversationsList,
        lastConversationId: shouldPreserveLastId
          ? state.lastConversationId
          : getLastConversationId(finalConversationsList),
        pinnedConversations: updatedPinnedConversations,
        totalConversations:
          finalConversationsList.length + updatedPinnedConversations.length,
        hasMoreConversations: state.hasMoreConversations,
      };
    });
  },
  removeConversationFromList: (conversationId) => {
    // Remove from pinned conversations if it exists
    if (get().removePinnedConversation(conversationId)) {
      return;
    }

    // Remove from regular conversations list
    let updatedConversationsList = get().conversationsList.filter(
      (conversation) => conversation.id !== conversationId
    );

    if (updatedConversationsList.length !== get().conversationsList.length) {
      updatedConversationsList = sortAndRemoveDuplicatesConversations(
        removePinnedConversations(
          updatedConversationsList,
          get().pinnedConversations
        )
      );

      set({
        conversationsList: updatedConversationsList,
        lastConversationId: getLastConversationId(updatedConversationsList),
      });
    }
  },
  updateConversation: (updatedConversation) => {
    // Update pinned conversation if it exists
    if (get().updatePinnedConversation(updatedConversation)) {
      return;
    }

    // Update in regular conversations list
    const regularIndex = get().conversationsList.findIndex(
      (conversation) => conversation.id === updatedConversation.id
    );

    if (regularIndex !== -1) {
      let updatedConversationsList = [...get().conversationsList];
      updatedConversationsList.splice(regularIndex, 1, updatedConversation);

      updatedConversationsList = sortAndRemoveDuplicatesConversations(
        removePinnedConversations(
          updatedConversationsList,
          get().pinnedConversations
        )
      );

      set({
        conversationsList: updatedConversationsList,
        lastConversationId: getLastConversationId(updatedConversationsList),
      });
    }
  },
  updateConversationFields: (conversationId, updatedConversationFields) => {
    // Update in pinned conversations
    if (
      get().updatePinnedConversationFields(
        conversationId,
        updatedConversationFields
      )
    ) {
      return;
    }

    // Update in regular conversations list
    const regularConversationIndex = get().conversationsList.findIndex(
      (conversation) => conversation.id === conversationId
    );

    if (regularConversationIndex === -1) {
      return;
    }

    const updatedConversation = {
      ...get().conversationsList[regularConversationIndex],
      ...updatedConversationFields,
    } as ConversationModel;

    if (
      !doesConversationMatchFilter(
        updatedConversation,
        get().conversationsListFilter
      )
    ) {
      get().removeConversationFromList(conversationId);
      return;
    }

    const updatedConversationsList = [...get().conversationsList];

    updatedConversationsList.splice(
      regularConversationIndex,
      1,
      updatedConversation
    );

    const newConversationsList = sortAndRemoveDuplicatesConversations(
      removePinnedConversations(
        updatedConversationsList,
        get().pinnedConversations
      )
    );

    set({
      conversationsList: newConversationsList,
      lastConversationId: getLastConversationId(newConversationsList),
    });
  },
  setIsLoadingConversationsList: (isLoading) =>
    set(() => ({
      isLoadingConversationsList: isLoading,
    })),
  setConversationsListFilter: (conversationsListFilter) =>
    set(() => ({
      ...initialState,
      ...pinnedConversationsSliceInitialState,
      conversationsListFilter: conversationsListFilter,
    })),
  setHasAttemptedConversationsFetch: (hasAttempted) =>
    set(() => ({ hasAttemptedConversationsFetch: hasAttempted })),
});
